import { Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PicklesService } from 'src/app/travelxnew/contracts/details/candidate-profile/pickles/pickles.service';
import { of } from 'rxjs';
import {
    getPickleCategories,
    getPickleCategoriesSuccess,
    getPickleCategoriesFailure,
    getPickleSources,
    getPickleSourcesSuccess,
    getPickleSourcesFailure,
    getPickleContractsByCandidateId,
    getPickleContractsByCandidateIdSuccess,
    getPickleContractsByCandidateIdFailure,
    getCandidatePickles,
    getCandidatePicklesFailure,
    getCandidatePicklesSuccess,
    submitPickle,
    submitPickleSuccess,
    submitPickleFailure,
    deletePickle,
    deletePickleSuccess,
    deletePickleFailure,
    clearSelectedContract,
    clearSelectedPickle
} from 'src/app/travelxnew/store/actions/pickles.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';

@Injectable()
export class PicklesEffects {
    constructor(private readonly _actions$: Actions, private readonly pickleService: PicklesService) {}

    getPickleCategories$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getPickleCategories),
            switchMap(() =>
                this.pickleService.getPickleCategories().pipe(
                    map((result) => getPickleCategoriesSuccess({ categories: result })),
                    catchError((error) =>
                        of(getPickleCategoriesFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                )
            )
        )
    );

    getPickleSources$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getPickleSources),
            switchMap(() => {
                return this.pickleService.getPickleSources().pipe(
                    map((result) => getPickleSourcesSuccess({ sources: result })),
                    catchError((error) =>
                        of(getPickleSourcesFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    getPickleContracts$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getPickleContractsByCandidateId),
            switchMap((action) => {
                return this.pickleService.getPickleContractsByCandidateId(action.candidateId).pipe(
                    map((result) => getPickleContractsByCandidateIdSuccess({ contracts: result })),
                    catchError((error) =>
                        of(
                            getPickleContractsByCandidateIdFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        )
    );

    getCandidatePickles$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getCandidatePickles, submitPickleSuccess, deletePickleSuccess),
            switchMap((action) => {
                return this.pickleService.getPicklesForCandidate(action.candidateId).pipe(
                    map((candidatePickles) => getCandidatePicklesSuccess({ candidatePickles })),
                    catchError((error) =>
                        of(getCandidatePicklesFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    submitPickle$ = createEffect(() =>
        this._actions$.pipe(
            ofType(submitPickle),
            switchMap((action) => {
                const candidateId = action.candidateId;
                return this.pickleService.submitPickle(candidateId, action.data).pipe(
                    map(() =>
                        submitPickleSuccess({
                            candidateId,
                            message: `Pickle successfully ${action.isEdit ? 'edited' : 'added'}`
                        })
                    ),
                    catchError((error) =>
                        of(submitPickleFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    deletePickle$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deletePickle),
            switchMap((action) => {
                return this.pickleService.deletePickle(action.pickleId).pipe(
                    map(() =>
                        deletePickleSuccess({ candidateId: action.candidateId, message: 'Pickle successfully deleted' })
                    ),
                    catchError((error) =>
                        of(deletePickleFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    pickleSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(submitPickleSuccess, deletePickleSuccess),
            map((action) => {
                clearSelectedContract();
                clearSelectedPickle();
                return showToasterMessage({ message: action.message });
            })
        )
    );

    pickleFailureToaster$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                getPickleCategoriesFailure,
                getPickleSourcesFailure,
                getPickleContractsByCandidateIdFailure,
                getCandidatePicklesFailure,
                submitPickleFailure,
                deletePickleFailure
            ),
            map((action) => showToasterMessage({ message: action.error }))
        )
    );
}
